import React from 'react';
import BlogLayout from '../../components/BlogLayout';

const OpenClawAttribution = () => {
    return (
        <BlogLayout
            title="Full Transparency: RoofClaw, OpenClaw, and Giving Credit Where It’s Due"
            date="Feb 21, 2026"
            readTime={3}
            heroImage="/images/blog/openclaw-attribution-hero.png"
            description="We are not the creators of the AI software. RoofClaw provides the hardware deployment and setup so you can leverage the brilliant open-source work of Peter Steinberger and the OpenClaw team."
        >
            <p className="lead text-xl text-[#F0EFF4] font-medium">
                As we gear up to launch RoofClaw and begin shipping pre-configured, zero-trust AI hardware nodes to roofing companies across the continent, there is one thing we need to make abundantly clear from day one:
            </p>
            <p className="font-bold text-[#7B61FF]">
                We did not build the AI software running on these machines. We don't own it. We didn't invent it. And we take absolutely zero credit for it.
            </p>
            <p>
                The true genius behind the artificial intelligence powering our nodes is the open-source project <strong>OpenClaw</strong>, and its incredible founder, <strong>Peter Steinberger</strong>, along with the vast community of developers contributing to it.
            </p>
            <p>
                We want to be 100% transparent about our relationship with the software, how our business model works within the constraints of their MIT License, and why we are doing this in the first place.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16 group-hover:text-[#7B61FF] transition-colors">Credit Where Credit Is Due</h2>
            <p>
                OpenClaw is a monumental achievement in the world of autonomous AI agents. The fact that it is released to the public under a permissive, open-source MIT License is a gift to the tech community. Recently, Peter Steinberger joined OpenAI, and the OpenClaw project is transitioning to a foundation to ensure it remains open and accessible.
            </p>
            <p>
                We have immense respect for Peter and his team. They built the engine. They wrote the code. They deserve all the accolades.
            </p>
            <p>
                We are not forking their code and pretending we made it. We are not burying their copyright notices. We are not claiming that OpenAI, the OpenClaw Foundation, or Peter Steinberger endorses RoofClaw.
            </p>
            <p className="font-bold text-xl font-drama italic text-[#F0EFF4]">
                The software is 100% theirs.
            </p>

            <hr className="my-16 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 group-hover:text-[#7B61FF] transition-colors">So, What Exactly Is RoofClaw Selling?</h2>
            <p>
                If the software is free and open-source, you might be wondering: <em>What am I actually paying $5,000 for?</em>
            </p>
            <p>
                We realized very quickly that while OpenClaw is incredibly powerful, the average roofing company owner does not have the time, the technical expertise, or the desire to:
            </p>
            <ol className="list-decimal pl-6 mb-8 text-[#F0EFF4]/80 space-y-2">
                <li>Source the correct, high-powered Apple Silicon hardware.</li>
                <li>Flash the system, secure the operating environment, and isolate the network.</li>
                <li>Configure the underlying API connections (OpenAI, Anthropic, etc.).</li>
                <li>Properly install and calibrate the OpenClaw environment so it runs flawlessly.</li>
                <li>Troubleshoot the command-line interfaces when things break.</li>
            </ol>
            <p>
                When you pay for a RoofClaw Node, you are paying for the physical Mac Mini (or MacBook), the intensive labor required to securely configure and lock down the machine, the shipping costs, and a two-part onboarding protocol. This includes a mandatory <strong>1-Hour Pre-Build Consultation</strong> to map your specific API and data needs, followed by a <strong>3-Hour Post-Delivery Training Session</strong> where we personally train you and your team on how to implement the AI into your specific roofing workflows (like JobNimbus and Slack integrations).
            </p>
            <p>
                The OpenClaw software itself remains free. You are paying us to take the technical friction out of the equation so you can plug the box in and immediately start acting on the intelligence.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16 group-hover:text-[#7B61FF] transition-colors">Honoring the MIT License</h2>
            <p>
                Before launching, we did our homework. We consulted with legal tools and reviewed the OpenClaw MIT License extensively.
            </p>
            <p>
                The MIT License is designed to be business-friendly. It explicitly allows third parties to bundle the software with hardware and charge for their labor and setup—provided that the original copyright notices and license texts are preserved.
            </p>
            <p>
                We are strictly adhering to these rules:
            </p>
            <ul className="list-disc pl-6 mb-8 text-[#F0EFF4]/80 space-y-4">
                <li><strong>Full Attribution:</strong> Every RoofClaw machine ships with the original OpenClaw MIT license and copyright notices fully intact in the software directory.</li>
                <li><strong>No Endorsement:</strong> We do not claim that OpenClaw endorses our hardware or our company. We are simply enthusiastic users and facilitators of their incredible open-source work.</li>
                <li><strong>Respect for AI Providers:</strong> We ensure that the usage of the underlying AI models (like Anthropic or OpenAI) adheres to their respective Terms of Service regarding API routing and multi-tenant systems.</li>
            </ul>

            <hr className="my-16 border-[#F0EFF4]/10" />

            <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-[2rem] p-10 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#7B61FF] opacity-10 blur-[100px] rounded-full mix-blend-screen pointer-events-none"></div>
                <h2 className="text-3xl font-heading font-bold mb-6">The Bottom Line</h2>
                <p className="text-lg text-[#F0EFF4]/80 mb-6">
                    Our mission is to bring absolute data sovereignty and cutting-edge operational AI to elite roofing companies. We saw a gap between brilliant open-source engineering and the practical, day-to-day realities of roofing contractors.
                </p>
                <p className="text-lg text-[#F0EFF4]/80 mb-6">
                    RoofClaw bridges that gap by providing the hardware and the setup. But the brain inside that hardware? That belongs entirely to Peter Steinberger and the OpenClaw team.
                </p>
                <p className="text-xl font-drama italic text-[#7B61FF] mb-6">
                    If you have the technical chops to buy a Mac Mini, secure the subnet, lock down the API routes, and install OpenClaw yourself—you absolutely should. The software is free. But if you want it done for you, securely, perfectly, and shipped ready-to-work to your front door?
                </p>
                <p className="text-lg text-[#F0EFF4]/80 mt-6 font-bold">
                    That's why we built RoofClaw.
                </p>
                <p className="text-sm text-[#F0EFF4]/40 mt-8 italic">
                    Thank you to Peter Steinberger and the OpenClaw community. We are honored to help deploy your life's work into the real world.
                </p>
            </div>
        </BlogLayout>
    );
};

export default OpenClawAttribution;
